import React, { Ref } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Tooltip,
} from '@mui/material';
import {
  ZicassoChat,
  OutlinedIcons,
  TemplatePluginHandle,
  ComposerRestoreContent,
} from '@marageti/z4-lib';
import {
  ConversationType, GetParticipantsResponse, Message, Conversation,
} from '@marageti/z4-sdk/lib/travel';
import { PersonType } from '@marageti/z4-sdk/lib/people';
import ChooseTemplateDialog from '../zicasso-dialogs/choose-template';
import AddDocumentsDialog from '../zicasso-dialogs/add-documents-dialog';
import AddItineraryDialog from '../zicasso-dialogs/add-itinerary';
import LogCallTextDialog from '../zicasso-dialogs/log-call';
import AddNoteDialog from '../zicasso-dialogs/note';
import { TwoWaySmsContainer } from '../two-way-sms';

type Props = {
  blockMessaging: boolean,
  closeDialog: () => void,
  conversation: Conversation | undefined,
  conversationType: ConversationType | undefined,
  handleViewClick: (message: Message) => void,
  isError: boolean,
  isInternal: boolean,
  isLoading: boolean,
  isValidating: boolean,
  messages: Message[] | undefined,
  lastMessageSeenId: string | undefined,
  loadMoreMessages: () => void,
  hasMoreMessages: boolean,
  openDialog: string,
  participants: GetParticipantsResponse | undefined,
  onSend: (text: string) => void,
  readOnly: boolean,
  setOpenDialog: (value: string) => void,
  setTemplateText: (text: string) => void,
  templatePluginRef: Ref<TemplatePluginHandle>,
  tripMatchId: string,
  viewer: {
    id: string | undefined,
    type: PersonType,
  },
  /** Refetch messages and trip match (status, sales stage) after upload from chat */
  afterTripMatchDocumentUpload: () => void | Promise<void>,
  updateMessages: () => void,
  travelerId: string | undefined,
  hasPartnerSmsNumber: boolean,
  hasTravelerPhoneNumber: boolean,
  isSmsOptedOut: boolean,
  isInternationalPhoneNumber: boolean,
  onUndoSend?: (message: Message) => void | Promise<void>,
  restoreEditorContent?: ComposerRestoreContent,
};

const ZicassoChatView = ({
  blockMessaging,
  closeDialog,
  conversation,
  conversationType,
  handleViewClick,
  isError,
  isInternal,
  isLoading,
  isValidating,
  messages,
  lastMessageSeenId,
  loadMoreMessages,
  hasMoreMessages,
  openDialog,
  participants,
  onSend,
  readOnly,
  setOpenDialog,
  setTemplateText,
  templatePluginRef,
  tripMatchId,
  viewer,
  afterTripMatchDocumentUpload,
  updateMessages,
  travelerId,
  hasPartnerSmsNumber,
  hasTravelerPhoneNumber,
  isSmsOptedOut,
  isInternationalPhoneNumber,
  onUndoSend = undefined,
  restoreEditorContent = undefined,
}: Props) => {
  let smsTooltipTitle = '';
  if (isSmsOptedOut) {
    smsTooltipTitle = 'Traveler opted out of SMS';
  }
  if (isInternationalPhoneNumber) {
    smsTooltipTitle = 'Cannot send SMS to international phone number';
  }
  const contextMenuItems = [
    {
      icon: <OutlinedIcons.OutlinedUploadIcon color="primary" />,
      action: () => setOpenDialog('addItinerary'),
      label: 'Upload Itinerary',
      id: 'uploadItinerary',
    },
    {
      icon: <OutlinedIcons.OutlinedUploadIcon color="primary" />,
      action: () => setOpenDialog('addDocuments'),
      label: 'Upload Travel Document',
      id: 'uploadTravelDocument',
    },
    {
      icon: <OutlinedIcons.OutlinedPencilAltIcon color="primary" />,
      action: () => setOpenDialog('addNote'),
      label: conversationType === ConversationType.ZicassoToPartner ? 'Log Note' : 'Log Internal Note',
      id: 'logNote',
    },
    {
      icon: <OutlinedIcons.OutlinedPhoneOutgoingIcon color="primary" />,
      action: () => setOpenDialog('logTextCall'),
      label: 'Log Call / Text Message',
      id: 'logCallTextMessage',
    },
    {
      icon: (
        <OutlinedIcons.OutlinedChatAlt2Icon color={isSmsOptedOut || isInternationalPhoneNumber ? 'disabled' : 'primary'} />
      ),
      action: () => {
        if (!isSmsOptedOut && !isInternationalPhoneNumber) {
          setOpenDialog('twoWaySms');
        }
      },
      label: (
        <Tooltip describeChild placement="top" title={smsTooltipTitle}>
          <Box component="span" sx={{ pointerEvents: isSmsOptedOut || isInternationalPhoneNumber ? 'auto' : undefined }}>
            Send SMS
          </Box>
        </Tooltip>
      ) as unknown as string,
      disabled: isSmsOptedOut || isInternationalPhoneNumber,
      id: 'twoWaySms',
    },
  ];

  const filteredContextMenuItems = isLoading ? [] : contextMenuItems.filter((item) => {
    if (conversationType !== ConversationType.TravelerToPartner) {
      return item.id !== 'uploadTravelDocument'
        && item.id !== 'uploadItinerary'
        && item.id !== 'twoWaySms';
    }
    if (item.id === 'twoWaySms' && (!hasPartnerSmsNumber || !hasTravelerPhoneNumber)) {
      return false;
    }
    return true;
  });

  return (
    <>
      {blockMessaging ? (
        <Alert severity="error">
          <AlertTitle>This Trip Has Not Been Accepted</AlertTitle>
          Once accepted, you will be able to message the traveler.
        </Alert>
      ) : (
        <ZicassoChat
          conversation={conversation}
          contextMenuItems={filteredContextMenuItems}
          insertTemplateProps={{
            onShowTemplateModal: () => setOpenDialog('chooseTemplate'),
            ref: templatePluginRef,
          }}
          isError={isError}
          isLoading={isLoading}
          isValidating={isValidating}
          messagingAlert={(
            <>
              Always communicate through this messaging platform to ensure communication is recorded.
            </>
          )}
          messages={messages}
          lastMessageSeenId={lastMessageSeenId}
          onSend={onSend}
          onLoadPreviousPage={loadMoreMessages}
          readOnly={readOnly}
          loadPreviousPages={hasMoreMessages}
          onViewClick={handleViewClick}
          participants={participants}
          viewer={viewer}
          onUndoSend={onUndoSend}
          restoreEditorContent={restoreEditorContent}
        />
      )}
      <ChooseTemplateDialog
        closeDialog={closeDialog}
        onSubmit={setTemplateText}
        open={openDialog === 'chooseTemplate'}
      />
      <AddDocumentsDialog
        closeDialog={closeDialog}
        onDocumentUploaded={afterTripMatchDocumentUpload}
        open={openDialog === 'addDocuments'}
        tripMatchId={tripMatchId}
      />
      <AddItineraryDialog
        closeDialog={closeDialog}
        onDocumentUploaded={afterTripMatchDocumentUpload}
        open={openDialog === 'addItinerary'}
        tripMatchId={tripMatchId}
      />
      <LogCallTextDialog
        closeDialog={closeDialog}
        open={openDialog === 'logTextCall'}
        onSuccess={updateMessages}
      />
      <AddNoteDialog
        closeDialog={closeDialog}
        isInternal={isInternal}
        onSuccess={updateMessages}
        open={openDialog === 'addNote'}
      />
      <TwoWaySmsContainer
        open={openDialog === 'twoWaySms'}
        closeDialog={closeDialog}
        travelerId={travelerId}
        conversationId={conversation?.id}
        tripMatchId={tripMatchId}
        onSuccess={updateMessages}
      />
    </>
  );
};

export default ZicassoChatView;
